import React from 'react';
import { Loader2, AlertCircle, CheckCircle2, XCircle, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import type { ProcessingStatus } from '@/stores/processingOverlayStore';

export type AgentProcessingStatus = ProcessingStatus;

interface AgentProcessingOverlayProps {
  isVisible: boolean;
  status?: AgentProcessingStatus;
  error?: string | null;
  onRetry?: () => void;
  onClose?: () => void;
  onMinimize?: () => void;
  minimizable?: boolean;
  title: string;
  description?: string;
  message?: string;
  processingMessage?: string;
  errorMessage?: string;
  variant?: 'fullscreen' | 'dialog';
  showProgress?: boolean;
  progress?: number;
  currentItem?: number;
  totalItems?: number;
}

export const AgentProcessingOverlay: React.FC<AgentProcessingOverlayProps> = ({
  isVisible,
  status = 'processing',
  error,
  onRetry,
  onClose,
  onMinimize,
  minimizable = true,
  title,
  description,
  message,
  processingMessage,
  errorMessage,
  variant = 'fullscreen',
  showProgress = false,
  progress = 0,
  currentItem = 0,
  totalItems = 0,
}) => {
  const { t } = useTranslation();

  const isFailed = status === 'error' || status === 'failed' || !!error;
  const isCompleted = !isFailed && status === 'completed';
  const isProcessing = status === 'processing' || status === 'queued';
  const canMinimize = minimizable && isProcessing && !!onMinimize;
  const hasItemProgress = totalItems > 0;

  const defaultProcessingMessage = t('agentProcessing.defaultProcessing');
  const defaultErrorMessage = t('agentProcessing.defaultError');

  if (!isVisible) return null;
  if (isCompleted && variant === 'dialog') return null;

  const minimizeButton = canMinimize ? (
    <button
      onClick={onMinimize}
      className="p-1.5 rounded-md hover:bg-muted transition-colors"
      title={t('agentProcessing.minimize')}
      data-tour-id="mi-processing-overlay-minimize"
    >
      <Minimize2 className="h-4 w-4 text-muted-foreground" />
    </button>
  ) : null;

  const errorContent = (
    <>
      <div className="mb-6">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-destructive/10 rounded-full mb-4">
          <XCircle className="h-10 w-10 text-destructive" />
        </div>
        <h3 className="text-xl font-semibold mb-2">
          {t('agentProcessing.error')}
        </h3>
      </div>

      <Alert variant="destructive" className="mb-6 text-left">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {error || errorMessage || defaultErrorMessage}
        </AlertDescription>
      </Alert>

      <div className="flex gap-3 justify-center">
        {onRetry && (
          <Button onClick={onRetry}>
            {t('agentProcessing.tryAgain')}
          </Button>
        )}
        {onClose && (
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        )}
      </div>
    </>
  );

  const completedContent = (
    <>
      <div className="mb-6">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/10 rounded-full mb-4">
          <CheckCircle2 className="h-10 w-10 text-green-500" />
        </div>
        <h3 className="text-xl font-semibold mb-2">
          {t('agentProcessing.completedTitle')}
        </h3>
        <p className="text-muted-foreground">
          {message || description || t('agentProcessing.completedMessage')}
        </p>
      </div>

      {onClose && (
        <Button onClick={onClose}>
          {t('agentProcessing.continue')}
        </Button>
      )}
    </>
  );

  const progressContent = hasItemProgress ? (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{t('agentProcessing.progress')}</span>
        <Badge variant="secondary" className="font-mono">
          {currentItem} / {totalItems}
        </Badge>
      </div>

      <Progress value={progress} className="h-3" />

      <div className="text-center">
        <span className="text-2xl font-bold text-primary">{progress}%</span>
      </div>

      {(message || processingMessage) && (
        <div className="bg-muted/50 rounded-lg px-4 py-3">
          <p className="text-sm text-foreground">{message || processingMessage}</p>
        </div>
      )}

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>{t('agentProcessing.pleaseWait')}</span>
      </div>
    </div>
  ) : (
    <>
      {showProgress && (
        <Progress value={undefined} className="w-full mb-4" />
      )}

      <div className="bg-muted/50 rounded-lg px-4 py-3 mb-4">
        <p className="text-sm font-medium text-foreground">
          {processingMessage || defaultProcessingMessage}
        </p>
      </div>

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>{t('agentProcessing.pleaseWait')}</span>
      </div>
    </>
  );

  const processingHeader = (
    <div className="mb-6">
      <div className={`inline-flex items-center justify-center ${hasItemProgress ? 'w-24 h-24' : 'w-20 h-20'} bg-primary/10 rounded-full mb-4 relative`}>
        <Loader2 className={`${hasItemProgress ? 'h-12 w-12' : 'h-10 w-10'} text-primary animate-spin`} />
        <div className="absolute inset-0 rounded-full bg-primary/20 animate-pulse" />
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      {!hasItemProgress && (
        <p className="text-muted-foreground">
          {description || processingMessage || defaultProcessingMessage}
        </p>
      )}
      {hasItemProgress && (
        <p className="text-muted-foreground text-sm">
          {t('agentProcessing.pleaseWait')}
        </p>
      )}
    </div>
  );

  const body = (
    <div className="text-center">
      {isFailed ? errorContent : isCompleted ? completedContent : (
        <>
          {processingHeader}
          {progressContent}
        </>
      )}
    </div>
  );

  if (variant === 'dialog') {
    return (
      <Dialog open={isVisible} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" hideCloseButton onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="sr-only">{title}</DialogTitle>
              <div />
              {minimizeButton}
            </div>
            <DialogDescription className="sr-only">{description}</DialogDescription>
          </DialogHeader>
          <div>
            {body}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 transition-all duration-400 bg-black/60 backdrop-blur-sm">
      <div
        className={`bg-background border border-border rounded-xl p-8 ${hasItemProgress ? 'max-w-lg' : 'max-w-md'} w-full mx-4 shadow-2xl relative transition-all duration-400 ease-in-out`}
      >
        {minimizeButton && (
          <div className="absolute top-3 right-3">
            {minimizeButton}
          </div>
        )}

        {body}
      </div>
    </div>
  );
};
